import { useQueries, type QueryFunctionContext } from "@tanstack/react-query";
import { useMemo } from "react";
import { useToast } from "@repo/ui";
import { configurationApi, fileApi } from "~/api";
import { folderKeys } from "~/components/side-bar/hooks/use-folder-mutations";
import { useSidebarContext } from "~/components/side-bar/sidebar-context";
import type { Folder } from "~/types/api";
import { sidebarStorage } from "~/utils/sidebar-storage";
import {
  transformBrandsToSidebarData,
  type NavItemPropsWithMetadata,
} from "~/utils/sidebar-transforms";
import { useMinimumLoading } from "./use-min-loading";

export const useSidebarData = () => {
  const { expandedIds } = useSidebarContext();
  const { showToast } = useToast();

  const {
    data: brands,
    isLoading: brandsLoading,
    error: brandsError,
  } = configurationApi.useQuery("get", "/api/Brand", undefined, {
    staleTime: 0,
    meta: { errorTitle: "Failed to load brands" },
    // TODO: fix permissions in blob storage to allow for brand logos to be displayed
    // Remove select when permissions are fixed and logos can be displayed
    select: (brands) => brands.map((brand) => ({ ...brand, logo: "" })),
  });

  const {
    data: tasks,
    isLoading: tasksLoading,
    error: tasksError,
  } = fileApi.useQuery("get", "/api/Task", undefined, {
    staleTime: 5 * 60 * 1000,
    meta: { errorTitle: "Failed to load tasks" },
  });

  const activeTasks = useMemo(() => {
    return tasks?.filter((task) => task.active !== false) || [];
  }, [tasks]);

  const expandedBrandIds = useMemo(() => {
    const brandIds: number[] = [];
    expandedIds.forEach((id) => {
      if (id.startsWith("brand-")) {
        const brandId = parseInt(id.replace("brand-", ""), 10);
        if (!isNaN(brandId) && brands?.some((brand) => brand.id === brandId)) {
          brandIds.push(brandId);
        }
      }
    });
    return brandIds;
  }, [expandedIds, brands]);

  const queryConfigs = useMemo(() => {
    return expandedBrandIds.map((brandId) => {
      return {
        queryKey: folderKeys.byBrand(brandId),
        queryFn: async ({ signal }: QueryFunctionContext) => {
          const isBackgroundRefetch = !!sidebarStorage.getCachedFolders(brandId);
          const startTime = Date.now();

          try {
            const response = await configurationApi.client.GET("/api/brand/{brandId}/Folder", {
              params: { path: { brandId } },
              signal,
            });
            if (response.error) {
              throw response.error;
            }

            const folders = response.data || [];

            if (!signal.aborted) {
              sidebarStorage.cacheFolders(brandId, folders);
            }

            if (!isBackgroundRefetch) {
              const elapsed = Date.now() - startTime;
              const minDuration = 400;
              if (elapsed < minDuration) {
                await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
              }
            }

            return folders;
          } catch (error) {
            if (signal.aborted) throw error;
            const errorMessage =
              error instanceof Error && error.message
                ? error.message
                : "An unexpected error occurred while fetching folders.";

            showToast("error", `Failed to load folders for brand ${brandId}`);
            console.error(`Failed to fetch folders for brand ${brandId}:`, error);
            throw new Error(errorMessage);
          }
        },
        initialData: () => sidebarStorage.getCachedFolders(brandId),
        staleTime: 0,
        enabled: brands !== undefined,
        retry: 2,
      };
    });
  }, [expandedBrandIds, brands]);

  const folderQueries = useQueries({
    queries: queryConfigs,
  });

  const foldersByBrand = useMemo(() => {
    const map = new Map<number, Folder[]>();

    expandedBrandIds.forEach((brandId, index) => {
      const query = folderQueries[index];
      const sorted = query?.data?.slice().sort((a, b) => a.name.localeCompare(b.name));

      if (query?.data) {
        map.set(brandId, sorted || []);
      }
    });

    brands?.forEach((brand) => {
      if (!map.has(brand.id)) {
        const cached = sidebarStorage
          .getCachedFolders(brand.id)
          ?.slice()
          .sort((a, b) => a.name.localeCompare(b.name));
        if (cached) {
          map.set(brand.id, cached);
        }
      }
    });

    return map;
  }, [expandedBrandIds, folderQueries, brands]);

  const loadingEntities = useMemo(() => {
    const loadingSet = new Set<string>();
    expandedBrandIds.forEach((brandId, index) => {
      const query = folderQueries[index];
      if (query?.isLoading) {
        loadingSet.add(`brand-${brandId}`);
      }
    });
    return loadingSet;
  }, [expandedBrandIds, folderQueries]);

  const sidebarData: NavItemPropsWithMetadata[] = useMemo(() => {
    if (!brands || !activeTasks) {
      return [];
    }

    return transformBrandsToSidebarData(
      brands,
      activeTasks,
      foldersByBrand,
      expandedIds,
      loadingEntities
    );
  }, [brands, activeTasks, foldersByBrand, expandedIds, loadingEntities]);

  const isLoadingRaw = brandsLoading || tasksLoading;

  const isLoading = useMinimumLoading(isLoadingRaw, 400);

  return {
    sidebarData,
    isLoading,
    hasErrors: brandsError || tasksError,
  };
};
